import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, KeyRound, LogOut } from "lucide-react";
import FittinMark from "@/components/fittin/FittinMark";
import { clearSession, useSession } from "@/lib/session";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isCompanies = location.pathname.startsWith("/companies");
  const session = useSession();

  const signOut = () => {
    clearSession({ userInitiated: true }); // /me sends a signed-out user home

  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else if (!isCompanies) navigate(`/${id}`);
  };

  const shopperLinks = [
    { label: "Returns", href: "#returns", scroll: true },
    { label: "AI assistant", href: "#mcp-teaser", scroll: true },
    { label: "Integration", href: "#integration", scroll: true },
    { label: "Philosophy", href: "#philosophy", scroll: true },
  ];

  const companyLinks = [
    { label: "How it works", href: "#how", scroll: true },
    { label: "Integration", href: "#integration", scroll: true },
    { label: "Philosophy", href: "#philosophy", scroll: true },
  ];

  const links = isCompanies ? companyLinks : shopperLinks;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-2 group"
        >
          <FittinMark size={24} />
          <span className="hidden min-[400px]:inline text-[17px] font-medium tracking-tight">Fittin</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => l.scroll && scrollTo(l.href)}
              className="text-[14px] text-neutral-600 hover:text-[#0A0A0A] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Audience switcher */}
          {isCompanies ? (
            <button
              data-testid="nav-switch-shoppers"
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white text-[#0A0A0A] text-[13px] px-3.5 py-2 hover:bg-neutral-50 transition-all"
            >
              For shoppers
            </button>
          ) : (
            <button
              data-testid="nav-switch-companies"
              onClick={() => navigate("/companies")}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white text-[#0A0A0A] text-[12.5px] sm:text-[13px] px-3 sm:px-3.5 py-2 hover:bg-neutral-50 transition-all whitespace-nowrap"
            >
              For companies
            </button>
          )}

          {!isCompanies && !session && (
            <Link
              to="/signin"
              data-testid="nav-signin"
              aria-label="Sign in with FitKey"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-black/10 bg-white text-[#0A0A0A] text-[13px] h-9 w-9 lg:w-auto lg:px-3.5 hover:bg-neutral-50 transition-all whitespace-nowrap"
            >
              <KeyRound className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden lg:inline">Sign in with FitKey</span>
            </Link>
          )}

          {!isCompanies && session ? (
            <>
              <Link
                to="/me"
                data-testid="nav-my-fitkey"
                className="group inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white text-[12.5px] sm:text-[13px] px-3 sm:px-4 py-2 whitespace-nowrap hover:bg-neutral-800 transition-all active:scale-[0.98]"
              >
                <KeyRound className="h-3.5 w-3.5" />
                My FitKey
              </Link>
              <button
                type="button"
                data-testid="nav-signout"
                aria-label="Sign out on this device"
                title="Sign out on this device"
                onClick={signOut}
                className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white h-9 w-9 hover:bg-neutral-50 transition-all"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
          <button
            data-testid="nav-cta"
            onClick={() =>
              isCompanies ? scrollTo("#contact") : scrollTo("#create-fitkey")
            }
            className="group inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white text-[12.5px] sm:text-[13px] px-3 sm:px-4 py-2 whitespace-nowrap hover:bg-neutral-800 transition-all active:scale-[0.98]"
          >
            {isCompanies ? "Book a pilot" : "Create your FitKey"}
            <ArrowUpRight className="hidden min-[400px]:block h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          )}
        </div>
      </div>
    </header>
  );
}
